"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseCreatedUpdatedFieldType = void 0;
const date_fns_1 = require("date-fns");
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const moment_1 = __importDefault(require("moment"));
const types_1 = require("@smartsuite/types");
const created_updated_1 = require("../../aggregations/created-updated");
const date_1 = require("../../aggregations/date");
const field_logic_1 = require("../../field-type-registry/field-logic");
const comparisons_1 = require("../../comparisons/comparisons");
const dom_1 = require("../../utils/dom");
const fields_1 = require("../../utils/fields");
const google_sheet_1 = require("../../utils/google-sheet");
const date_2 = require("../../formatters/date");
const base_created_updated_fieldtype_1 = require("../../safe-field-value/date-fields/created-updated-fields/base-created-updated.fieldtype");
const utils_1 = require("../../spotlight/utils");
const date_3 = require("../../utils/date");
const member_1 = require("../../utils/member");
const record_1 = require("../../utils/record");
const texts_1 = require("../../texts");
const intl_1 = require("../../locale/intl");
const date_4 = require("../../filter/date");
const validation_1 = require("../../validation/validation");
class BaseCreatedUpdatedFieldType extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.aggregates = {
            empty: { label: (0, intl_1.$t)(texts_1.aggregatesText.empty), aggregator: field_logic_1.countEmpty },
            "non-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.non_empty),
                aggregator: field_logic_1.countFilled,
            },
            "percent-filled": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_filled),
                aggregator: field_logic_1.percentFilled,
            },
            "percent-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_empty),
                aggregator: field_logic_1.percentEmpty,
            },
            "earliest-date": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.earliest_date),
                aggregator: (0, created_updated_1.createdUpdatedAggregator)(date_1.minDate),
            },
            "latest-date": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.latest_date),
                aggregator: (0, created_updated_1.createdUpdatedAggregator)(date_1.maxDate),
            },
            "date-range": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.date_range),
                aggregator: (0, created_updated_1.createdUpdatedAggregator)((dates, ...props) => {
                    if (!dates.length)
                        return null;
                    return `${(0, date_1.minDate)(dates, ...props)} - ${(0, date_1.maxDate)(dates, ...props)}`;
                }),
            },
        };
        this.isDate = true;
        this.placeholder = (0, intl_1.$t)(texts_1.createUpdateFieldText.select_member);
        this.fieldDisabledMessage = (0, intl_1.$t)(texts_1.createUpdateFieldText.this_is_a_system_field);
        this.uniqueByApplication = true;
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is: comparisons_1.isDate,
            is_not: comparisons_1.isNotDate,
            is_before: comparisons_1.isBefore,
            is_after: comparisons_1.isAfter,
            is_on_or_before: comparisons_1.isOnOrBefore,
            is_on_or_after: comparisons_1.isOnOrAfter,
            contains: comparisons_1.contains,
        };
        this.groupByMode = "groupBy";
        this.fieldValueGetter = new base_created_updated_fieldtype_1.BaseCreatedUpdatedFieldValueGetter();
    }
    get validators() {
        return { required: validation_1.required };
    }
    getFilterComponentData(field, comparison) {
        return {
            ...super.getFilterComponentData(field, comparison),
            dateModes: this.dateModes,
            placeholder: comparison === "contains"
                ? (0, intl_1.$t)(texts_1.createUpdateFieldText.type_member_name)
                : (0, intl_1.$t)(texts_1.createUpdateFieldText.type_number_of_days),
        };
    }
    canModifySettings(_field) {
        return true;
    }
    sortKey(fieldValue) {
        return fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.on;
    }
    groupByKey(fieldValue, field, userPreferences) {
        if (!(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.on)) {
            return "";
        }
        const date = (0, date_3.getDateTime)(fieldValue.on, userPreferences);
        if (field.params.date_format === "days_ago") {
            return (0, date_3.formatIsoWithTzOffset)((0, date_3.getStartOfDistanceForDate)(date, userPreferences));
        }
        return (0, date_3.formatIsoWithTzOffset)((0, date_fns_1.startOfDay)(date));
    }
    groupBySortKey(value, field, userPreferences) {
        return this.groupByKey(value, field, userPreferences);
    }
    getSortCacheKey(field, fieldValue, rule, isGroupByRule) {
        return `${field.slug}:${String(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.on)}:${rule.field}:${rule.direction}:${String(isGroupByRule)}`;
    }
    getEmptyValue() {
        return {
            by: null,
            on: "",
        };
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            editorWidth: "100%",
            allowAddRecordToGroup: false,
        };
    }
    processFilterValue(filterValue, comparison, _field, userPreferences) {
        const emptyComparisons = ["is_empty", "is_not_empty"];
        const containsComparisons = ["contains", "not_contains"];
        if (!emptyComparisons.includes(comparison) &&
            !containsComparisons.includes(comparison)) {
            const helper = new date_4.BaseDateModeHelper(comparison, filterValue.date_mode, filterValue.date_mode_value, userPreferences);
            return helper.getValue(filterValue === null || filterValue === void 0 ? void 0 : filterValue.date_mode);
        }
        return filterValue;
    }
    filterValueToString(filterValue, field, userPreferences, comparison) {
        const containsComparisons = ["contains", "not_contains"];
        if (containsComparisons.includes(comparison)) {
            return filterValue;
        }
        return filterValue
            ? (0, utils_1.dateFieldName)(field, {
                value: filterValue,
                comparison,
                field: field.field_type,
            }, userPreferences)
            : filterValue;
    }
    valueToString(fieldValue, field, userPreferences, record) {
        var _a, _b, _c, _d, _e;
        if (!(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.on)) {
            return "";
        }
        const dateValue = (0, date_3.toMomentDate)(fieldValue.on, (_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale.value) !== null && _a !== void 0 ? _a : "", (_b = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.timezone.value) !== null && _b !== void 0 ? _b : "", true);
        const userValue = this.getUserValue(record, field);
        const dateString = (field === null || field === void 0 ? void 0 : field.params.date_format) === "days_ago"
            ? (0, moment_1.default)(dateValue).fromNow()
            : `on ${(0, date_2.formatDateTime)(dateValue, (_c = field.params.include_time) !== null && _c !== void 0 ? _c : false, userPreferences)}`;
        const member = (_e = (_d = userValue === null || userValue === void 0 ? void 0 : userValue.full_name) === null || _d === void 0 ? void 0 : _d.sys_root) !== null && _e !== void 0 ? _e : (0, intl_1.$t)(texts_1.createUpdateFieldText.unknown);
        return (0, intl_1.$t)(texts_1.createUpdateFieldText.by_member_dateString, {
            member,
            dateString,
        });
    }
    getContentSize(fieldValue, field, userPreferences, record, font) {
        if (!(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.on)) {
            return 0;
        }
        const { date_format, display_format } = field.params;
        const dateString = date_format === "days_ago"
            ? (0, date_3.formatDistanceToNow)(fieldValue.on, userPreferences)
            : (0, date_2.formatDateTime)(fieldValue.on, !!field.params.include_time, userPreferences);
        const member = this.getUserValue(record, field);
        const isAvatar = display_format === "avatar";
        const isAvatarDate = display_format === "avatar_date";
        const hasDate = display_format === "date";
        const hasAvatar = (isAvatar || isAvatarDate) && member;
        const isDateView = hasDate || isAvatarDate;
        const initials = hasAvatar ? (0, member_1.memberInitials)(member) : "";
        let strValue = "";
        if (hasAvatar)
            strValue += initials;
        if (isDateView)
            strValue += strValue ? ` ${dateString}` : dateString;
        return (0, dom_1.getTextSize)(strValue, undefined, font);
    }
    valueToGoogleSheet(fieldValue, field, userPreferences, record) {
        var _a, _b, _c, _d;
        if (!(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.on)) {
            return (0, google_sheet_1.makeRowDataValues)("", "stringValue");
        }
        const dateValue = (0, date_3.toMomentDate)(fieldValue.on, (_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale.value) !== null && _a !== void 0 ? _a : "", (_b = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.timezone.value) !== null && _b !== void 0 ? _b : "", field.params.include_time);
        const dateFormat = new date_2.DateFieldValueFormatter().prepareFormat(dateValue, "standard", userPreferences);
        const userValue = this.getUserValue(record, field);
        const member = (_d = (_c = userValue === null || userValue === void 0 ? void 0 : userValue.full_name) === null || _c === void 0 ? void 0 : _c.sys_root) !== null && _d !== void 0 ? _d : (0, intl_1.$t)(texts_1.createUpdateFieldText.unknown);
        const timeFormat = (userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale.time_format) === "24h" ? "hh:mm" : "hh:mm am/pm";
        const pattern = (0, intl_1.$t)(texts_1.createUpdateFieldText.by_member_on, {
            member,
            dateFormat,
            timeFormat,
        });
        const date = fieldValue.on.split("T");
        const dateString = date[0];
        const timeString = date[1].slice(0, -1);
        const value = `=DATEVALUE("${dateString}") + TIMEVALUE("${timeString}")`;
        return (0, google_sheet_1.makeRowDataValues)(value, "formulaValue", "DATE", pattern);
    }
    processFieldValue(record, slug, comparison, field, userPreferences) {
        const containsComparisons = ["contains", "not_contains"];
        if (containsComparisons.includes(comparison)) {
            const member = this.getUserValue(record, field);
            return (0, record_1.getTitleOrUntitledRecordString)(member);
        }
        const fieldValue = super.processFieldValue(record, slug, comparison, field);
        const isoDate = (fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.on) || new Date().toISOString();
        return (0, date_3.getDateTime)(isoDate, userPreferences);
    }
    searchKey(fieldValue, field, userPreferences) {
        var _a;
        if (fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.on) {
            const dateValue = (0, date_3.toMomentDate)(fieldValue.on, userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale.value, userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.timezone.value, field.params.include_time);
            return (field === null || field === void 0 ? void 0 : field.params.date_format) === "days_ago"
                ? (0, moment_1.default)(dateValue).fromNow()
                : (0, date_2.formatDateTime)(dateValue, (_a = field.params.include_time) !== null && _a !== void 0 ? _a : false, userPreferences);
        }
        return "";
    }
    getViewControlRules(field) {
        return {
            ...super.getViewControlRules(field),
            nestedFieldsInFieldsToDisplay: "hide",
            nestedFieldsInSort: "hide",
            nestedFieldsInFilter: "hide",
            nestedFieldsInGroup: "hide",
            nestedFieldsInSpotlight: "hide",
            groupBy: { direction: "desc" },
        };
    }
    prepareValueForClipboard(fieldValue, srcField, userPreferences, record) {
        const rawValue = {
            ...this.getUserValue(record, srcField),
            date: {
                date: fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.on,
                include_time: srcField.params.include_time,
            },
        };
        return {
            text: this.valueToString(fieldValue, srcField, userPreferences, record),
            rawValue,
        };
    }
    canHaveReferencedRecords() {
        return true;
    }
    getReferencedRecords(fieldValue) {
        const ids = fieldValue ? [fieldValue.by] : [];
        return ids.map((id) => {
            return {
                recordId: id,
                application_slug: types_1.MEMBERS_APP_SLUG,
            };
        });
    }
    isValueEqual(fieldValue1, fieldValue2) {
        return (0, react_fast_compare_1.default)(fieldValue1, fieldValue2);
    }
    canChangeValue(_field, fromValue, toValue) {
        return this.isValueEqual(fromValue, toValue);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 140,
            minWidth: 70,
        };
    }
    getRecordTitleVariableOptions(field) {
        return [
            {
                title: (0, intl_1.$t)(texts_1.createUpdateFieldText.fieldLabel_by, {
                    fieldLabel: field.label,
                }),
                value: `${field.slug}.by`,
                icon: "user",
            },
            {
                title: (0, intl_1.$t)(texts_1.createUpdateFieldText.fieldLabel_on, {
                    fieldLabel: field.label,
                }),
                value: `${field.slug}.on`,
                icon: "date-select",
            },
        ];
    }
    getRecordTitleVariableValue(variableName, fieldValue, field, userPreferences, record) {
        var _a, _b, _c;
        if (variableName === `${field.slug}.by`) {
            const member = this.getUserValue(record, field);
            return (_b = (_a = member === null || member === void 0 ? void 0 : member.full_name) === null || _a === void 0 ? void 0 : _a.sys_root) !== null && _b !== void 0 ? _b : "";
        }
        if (variableName === `${field.slug}.on`) {
            return (0, date_2.formatDateTime)(fieldValue.on, (_c = field.params.include_time) !== null && _c !== void 0 ? _c : false, userPreferences);
        }
        return "";
    }
    getEmptyFilterValue(_field, comparison) {
        var _a;
        const comparisonsWithNullValue = ["is_empty", "is_not_empty"].includes(comparison);
        if (comparisonsWithNullValue) {
            return null;
        }
        const comparisonsWithTextValue = ["contains", "not_contains"].includes(comparison);
        if (comparisonsWithTextValue) {
            return "";
        }
        const dateMode = (_a = this.dateModes[comparison]) !== null && _a !== void 0 ? _a : this.dateModes.default;
        if (!dateMode) {
            return null;
        }
        const defaultDateComparison = dateMode[0];
        return (0, date_4.getDateEmptyFilterValue)(defaultDateComparison.value, defaultDateComparison.label);
    }
    getUserValue(record, field) {
        if (!record) {
            return;
        }
        const value = (0, fields_1.getValueFromRecordByField)(record, field);
        if (!value.by) {
            return;
        }
        const member = this.appRecordsService.getRelatedRecord(value.by);
        return member && !(0, record_1.isRecordDeleted)(member) ? member : undefined;
    }
    isFieldPermissionAllowed() {
        return false;
    }
}
exports.BaseCreatedUpdatedFieldType = BaseCreatedUpdatedFieldType;
